/**
 * Workspace 一覧のフィルタバー (OPS-WS-2 / OPS-WS-4)。
 * sales_counter 単体前提へ縮退（#1479、カウンターシリーズ4製品構想は2026-07-02撤退。
 * 単一製品では製品切り替え自体が意味を成さないため製品セレクタは削除済）。
 *
 * - 課金状態 / 健康度 / 検索を URL クエリで保持
 * - 健康度は複数選択可（OR セマンティクス）
 */
import type { BillingStatus, HealthFilterKey, WorkspaceListParams } from '../../lib/workspaces-api';
import { BILLING_STATUS_LABEL, HEALTH_LABEL } from './labels';

export interface WorkspaceFiltersBarProps {
  params: WorkspaceListParams;
  onChange: (next: WorkspaceListParams) => void;
}

const STATUSES: ReadonlyArray<BillingStatus> = ['trialing', 'active', 'past_due', 'canceled'];

const HEALTH_KEYS: ReadonlyArray<HealthFilterKey> = [
  'past_due',
  'inactive_14d_ai_off',
  'trial_no_lead_7d',
  'no_channel',
  'no_lead_30d',
];

export function WorkspaceFiltersBar({ params, onChange }: WorkspaceFiltersBarProps) {
  const toggleHealth = (key: HealthFilterKey) => {
    const cur = params.health ?? [];
    const next = cur.includes(key) ? cur.filter((h) => h !== key) : [...cur, key];
    onChange({ ...params, health: next.length > 0 ? next : undefined, page: 1 });
  };

  return (
    <section
      data-testid="workspace-filters-bar"
      className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          <span>課金状態</span>
          <select
            aria-label="課金状態フィルタ"
            value={params.status ?? ''}
            onChange={(e) =>
              onChange({
                ...params,
                status: (e.target.value || undefined) as BillingStatus | undefined,
                page: 1,
              })
            }
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">全て</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {BILLING_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col text-xs text-slate-600">
          <span>検索（ws 名 / slug）</span>
          <input
            aria-label="ワークスペース検索"
            type="text"
            value={params.q ?? ''}
            onChange={(e) => onChange({ ...params, q: e.target.value || undefined, page: 1 })}
            placeholder="acme, sakura..."
            className="mt-1 w-56 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
      </div>

      <div className="mt-4">
        <p className="text-xs text-slate-600">健康度フィルタ（複数選択可、OR）</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {HEALTH_KEYS.map((key) => {
            const active = (params.health ?? []).includes(key);
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => toggleHealth(key)}
                className={
                  active
                    ? 'rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs text-rose-700'
                    : 'rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-600 hover:border-slate-400'
                }
              >
                {HEALTH_LABEL[key]}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
